import { Path } from "../../../static/Constants";
import { colors } from "../../../theme";
import Button from "../ui/inputs/CommonButton";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { LogOut, Wallet } from "lucide-react";
import { useAccount } from "../../Context/AccountContext";
import { displayBalance } from "../../../static/balance";
import { apiError } from "../../Service/Api";

export default function AccountNav({ onAction, sx }) {
  const { user, wallet, loading, walletError, signout } = useAccount();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const handleAction = () => {
    if (typeof onAction === "function") {
      onAction();
    }
  };
  const logout = async () => {
    setBusy(true);
    setError("");
    try {
      await signout();
      handleAction();
    } catch (failure) {
      setError(apiError(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        ...sx,
      }}
    >
      {loading ? (
        <CircularProgress size={20} aria-label="Checking account" />
      ) : user ? (
        <>
          <Tooltip
            title={
              walletError
                ? "Balance unavailable. Open wallet to retry."
                : `${displayBalance(
                    wallet?.balance
                  )} credits — open wallet for ${user.name}`
            }
          >
            <Button
              variant="text"
              component={Link}
              to={Path.wallet}
              onClick={handleAction}
              aria-label="Open wallet"
              startIcon={<Wallet size={17} />}
              sx={{
                textTransform: "none",
                bgcolor: colors.primary.lighter,
                color: colors.primary.dark,
                borderRadius: 2,
                px: { xs: 1, sm: 1.5 },
                whiteSpace: "nowrap",
                fontSize: 13,
                minWidth: 0,
                maxWidth: { xs: 100, sm: 260 },
              }}
            >
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {walletError
                  ? "Retry balance"
                  : `${displayBalance(wallet?.balance)} credits`}
              </Box>
            </Button>
          </Tooltip>
          <Tooltip title={`Sign out ${user.email}`}>
            <IconButton
              aria-label="Sign out"
              onClick={logout}
              disabled={busy}
              size="small"
            >
              <LogOut size={17} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <Button
            variant="text"
            component={Link}
            to={Path.login}
            onClick={handleAction}
            size="small"
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: 0,
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: 12, sm: 13 },
            }}
          >
            Sign in
          </Button>
          <Button
            component={Link}
            to={Path.register}
            onClick={handleAction}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              borderRadius: 2,
              minWidth: 0,
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: 12, sm: 13 },
            }}
          >
            Sign up
          </Button>
        </>
      )}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ position: "fixed", top: 70, right: 12, maxWidth: 330 }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
}
